import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Popover } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { AllPlanScheduleType } from '@demind-inc/core';

import {
  planMyDayIntroSeedDateAtom,
  selectedDateAtom,
  useAssistantChat,
  usePrepareScheduler,
} from '../../../data-access';
import {
  useAllPlanSuggestedModal,
  useAIScheduler,
  useDailyPrefSchedulerChat,
} from '../../../hooks';
import {
  transformRoutineForEndpoint,
  transformTaskPrefForEndpoint,
} from '../../Scheduler/AIScheduler/reducer';
import { isWorkoutEnabledForDate } from '../../../helpers/workoutPlanDefaults.helpers';
import { ReactComponent as ActionEntireDay } from '../../../assets/chat/action_entire_day.svg';
import PlanTypePill from './PlanTypePill';
import AddPreferenceChip from './AddPreferenceChip';
import PlanTypeDetailDrawer from './PlanTypeDetailDrawer';
import './PlanIntroBubble.scss';

type PlanTypeWithDetail = Exclude<AllPlanScheduleType, 'sleep'>;

const ANCHOR_PLAN_TYPES: AllPlanScheduleType[] = ['meal', 'sleep', 'workout'];

const canExpandPlanType = (type: AllPlanScheduleType): type is PlanTypeWithDetail =>
  type !== 'sleep';

const resolveIntroPlanDate = (seedDate: string | null, selectedDate: Dayjs | null | undefined) => {
  if (seedDate) {
    const seeded = dayjs(seedDate);
    if (seeded.isValid() && !seeded.isBefore(dayjs(), 'day')) {
      return seeded;
    }
  }
  const base = selectedDate?.isValid?.() ? selectedDate : dayjs();
  return base.isBefore(dayjs(), 'day') ? dayjs() : base;
};

export type PlanIntroConfirmState = {
  canConfirm: boolean;
  isConfirming: boolean;
  confirm: () => void;
};

interface PlanIntroBubbleProps {
  /** Parent renders Run/back footer; bubble publishes confirm readiness here. */
  onConfirmStateChange?: (state: PlanIntroConfirmState | null) => void;
}

/**
 * "What would you like to plan today?" card, rendered inline in the chat
 * thread (message.planIntro === true) — the same toggle/tasks/routines/date
 * picker/comment UI as the old PlanMyDayIntroModal, minus the modal chrome
 * (no visibility atom, no close button: it's a permanent part of the chat
 * history once shown). Confirming calls generatePlanFromIntro instead of
 * manually toggling the all-plan-panel-open atom.
 *
 * Meal / sleep / workout defaults follow lifestack_calendar PlanMyDayIntroModal:
 * `defaultRerunTypes` (already-planned + already-on-calendar anchors) and
 * `isWorkoutEnabledForDate` for the selected plan day.
 */
const PlanIntroBubble: FC<PlanIntroBubbleProps> = ({ onConfirmStateChange }) => {
  const { t } = useTranslation();
  const [introSeedDate, setIntroSeedDate] = useRecoilState(planMyDayIntroSeedDateAtom);
  const selectedDate = useRecoilValue(selectedDateAtom);
  const { generatePlanFromIntro } = useAssistantChat();
  const { routine } = usePrepareScheduler();

  const { availablePlanTypes, defaultRerunTypes, skipAllPlanning } = useAllPlanSuggestedModal();
  const { handleRunAIScheduler } = useAIScheduler();

  const [planDate, setPlanDate] = useState<Dayjs>(() =>
    resolveIntroPlanDate(introSeedDate, selectedDate)
  );
  const [selectedPlanTypes, setSelectedPlanTypes] = useState<AllPlanScheduleType[]>([]);
  const [expandedType, setExpandedType] = useState<AllPlanScheduleType | null>(null);
  const [expandAnchorEl, setExpandAnchorEl] = useState<HTMLElement | null>(null);
  const [customText, setCustomText] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [datePickerAnchor, setDatePickerAnchor] = useState<null | HTMLElement>(null);
  const userAdjustedAnchorsRef = useRef(false);

  const taskScheduler = useDailyPrefSchedulerChat({
    selectedDate: planDate,
    handleRunAIScheduler,
  });

  const planDateKey = planDate.format('YYYY-MM-DD');

  const isWorkoutOnForPlanDate = useMemo(
    () => isWorkoutEnabledForDate(routine?.routineActivities, planDate),
    [planDate, routine?.routineActivities]
  );

  // Same default selection source as lifestack_calendar PlanMyDayIntroModal /
  // PlanIntroBubble: skip anchors already planned or present, and skip workout
  // when the workout routine is disabled for this day.
  const defaultPlanTypesForDate = useMemo(
    () => defaultRerunTypes.filter((type) => type !== 'workout' || isWorkoutOnForPlanDate),
    [defaultRerunTypes, isWorkoutOnForPlanDate]
  );

  const selectedTasksCount = useMemo(
    () => Object.values(taskScheduler.tasksPrefItems || {}).filter((task) => task.selected).length,
    [taskScheduler.tasksPrefItems]
  );
  const selectedRoutineCount = useMemo(
    () =>
      Object.entries(taskScheduler.routinePrefItem || {}).filter(([activityId, activity]) => {
        if (taskScheduler.scheduledRoutineActivityIds.has(activityId)) {
          return false;
        }
        return activity.selected !== false;
      }).length,
    [taskScheduler.routinePrefItem, taskScheduler.scheduledRoutineActivityIds]
  );

  const planTypeCounts: Partial<Record<AllPlanScheduleType, number>> = {
    todo: selectedPlanTypes.includes('todo') ? selectedTasksCount : 0,
    routine: selectedPlanTypes.includes('routine') ? selectedRoutineCount : 0,
  };

  const planDateLabel = useMemo(() => {
    if (planDate.isSame(dayjs(), 'day')) {
      return t('calendar.today', { defaultValue: 'Today' });
    }
    if (planDate.isSame(dayjs().add(1, 'day'), 'day')) {
      return t('calendar.tomorrow', { defaultValue: 'Tomorrow' });
    }
    return planDate.format('MMM D');
  }, [planDate, t]);

  // Consume the seed date once, on mount only — this card is created once per
  // Plan My Day intro trigger and doesn't need to keep re-seeding afterwards.
  useEffect(() => {
    if (introSeedDate) {
      setIntroSeedDate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    userAdjustedAnchorsRef.current = false;
  }, [planDateKey]);

  // Seed meal/sleep/workout from defaultRerunTypes (+ workout enabled), matching
  // calendar PlanMyDayIntroModal. Preserve todo/routine for the count sync effect.
  useEffect(() => {
    if (userAdjustedAnchorsRef.current) {
      return;
    }
    setSelectedPlanTypes((prev) => {
      const todoRoutine = prev.filter((type) => type === 'todo' || type === 'routine');
      const anchors = defaultPlanTypesForDate.filter((type) => ANCHOR_PLAN_TYPES.includes(type));
      const next = [...anchors, ...todoRoutine];
      if (
        next.length === prev.length &&
        next.every((type) => prev.includes(type)) &&
        prev.every((type) => next.includes(type))
      ) {
        return prev;
      }
      return next;
    });
  }, [defaultPlanTypesForDate]);

  // If the selected date has workout off, deselect it by default (calendar parity).
  useEffect(() => {
    if (isWorkoutOnForPlanDate) {
      return;
    }
    setSelectedPlanTypes((prev) =>
      prev.includes('workout') ? prev.filter((type) => type !== 'workout') : prev
    );
  }, [isWorkoutOnForPlanDate]);

  useEffect(() => {
    if (taskScheduler.isInitializing) {
      return;
    }
    setSelectedPlanTypes((prev) => {
      let next = prev;
      if (selectedTasksCount > 0 && !next.includes('todo')) {
        next = [...next, 'todo'];
      } else if (selectedTasksCount === 0 && next.includes('todo')) {
        next = next.filter((type) => type !== 'todo');
      }
      if (selectedRoutineCount > 0 && !next.includes('routine')) {
        next = [...next, 'routine'];
      } else if (selectedRoutineCount === 0 && next.includes('routine')) {
        next = next.filter((type) => type !== 'routine');
      }
      return next;
    });
  }, [selectedRoutineCount, selectedTasksCount, taskScheduler.isInitializing]);

  const closeExpandPopover = () => {
    setExpandedType(null);
    setExpandAnchorEl(null);
  };

  const handleExpandPress = (type: AllPlanScheduleType, event: React.MouseEvent<HTMLElement>) => {
    setExpandAnchorEl(event.currentTarget);
    setExpandedType(type);
  };

  const togglePlanType = (type: AllPlanScheduleType) => {
    if (type === 'workout' && !isWorkoutOnForPlanDate) {
      return;
    }
    if (ANCHOR_PLAN_TYPES.includes(type)) {
      userAdjustedAnchorsRef.current = true;
    }
    setSelectedPlanTypes((prev) =>
      prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type]
    );
  };

  const isSchedulerPrefsLoading = taskScheduler.isInitializing;

  const handleConfirm = useCallback(async () => {
    // Wait until todo/routine reducers have initialized for planDate — otherwise we
    // serialize empty prefs and pass them as explicit v8 overrides, bypassing fallbacks.
    if (isConfirming || !selectedPlanTypes.length || skipAllPlanning || isSchedulerPrefsLoading) {
      return;
    }
    setIsConfirming(true);

    const targetDate = planDate.format('YYYY-MM-DD');
    const focusAreas = customText.trim() ? [customText.trim()] : [];
    const selection = {
      todos: selectedPlanTypes.includes('todo')
        ? transformTaskPrefForEndpoint(taskScheduler.tasksPrefItems, targetDate).filter(
            (task) => task.selected
          )
        : undefined,
      routine: selectedPlanTypes.includes('routine')
        ? transformRoutineForEndpoint(taskScheduler.routinePrefItem, planDate).filter(
            (activity) =>
              (activity as { selected?: boolean }).selected !== false &&
              !taskScheduler.scheduledRoutineActivityIds.has(activity.routineActivityId)
          )
        : undefined,
    };

    try {
      await generatePlanFromIntro(selectedPlanTypes, focusAreas, targetDate, selection);
    } finally {
      setIsConfirming(false);
    }
  }, [
    customText,
    generatePlanFromIntro,
    isConfirming,
    isSchedulerPrefsLoading,
    planDate,
    selectedPlanTypes,
    skipAllPlanning,
    taskScheduler.routinePrefItem,
    taskScheduler.scheduledRoutineActivityIds,
    taskScheduler.tasksPrefItems,
  ]);

  const canConfirm =
    selectedPlanTypes.length > 0 && !isConfirming && !isSchedulerPrefsLoading && !skipAllPlanning;

  useEffect(() => {
    if (!onConfirmStateChange) {
      return;
    }
    if (skipAllPlanning) {
      onConfirmStateChange(null);
      return;
    }
    onConfirmStateChange({
      canConfirm,
      isConfirming,
      confirm: () => {
        void handleConfirm();
      },
    });
  }, [canConfirm, handleConfirm, isConfirming, onConfirmStateChange, skipAllPlanning]);

  useEffect(() => {
    return () => {
      onConfirmStateChange?.(null);
    };
  }, [onConfirmStateChange]);

  if (skipAllPlanning) {
    return null;
  }

  return (
    <>
      <div className="plan-intro-bubble" aria-labelledby="plan-intro-bubble-title">
        <div className="plan-intro-bubble__body">
          <div className="plan-intro-bubble__card">
            <div className="plan-intro-bubble__planner-label">
              <ActionEntireDay
                style={{ color: '#C9B8FF' }}
                className="plan-intro-bubble__planner-icon"
              />
              <span id="plan-intro-bubble__label-title">
                {t('planMyDayIntro.aiPlannerLabel', { defaultValue: 'AI Planner' })}
              </span>
            </div>

            <div className="plan-intro-bubble__sentence">
              <span className="plan-intro-bubble__inline-text">
                {t('planMyDayIntro.iWantAiToPlan', { defaultValue: 'I want to plan my' })}
              </span>
              {availablePlanTypes.map((type, index) => (
                <React.Fragment key={type}>
                  {index > 0 && (
                    <span className="plan-intro-bubble__inline-text">
                      {index === availablePlanTypes.length - 1
                        ? t('task.ai.and', { defaultValue: 'and' })
                        : ','}
                    </span>
                  )}
                  <PlanTypePill
                    label={t(`task.allPlan.planTypeLabels.${type}`)}
                    selected={selectedPlanTypes.includes(type)}
                    onPress={(event) => {
                      if (type === 'todo' || type === 'routine') {
                        handleExpandPress(type, event);
                        return;
                      }
                      togglePlanType(type);
                    }}
                    onExpandPress={
                      canExpandPlanType(type) && type !== 'todo' && type !== 'routine'
                        ? (event) => handleExpandPress(type, event)
                        : undefined
                    }
                    count={planTypeCounts[type]}
                  />
                </React.Fragment>
              ))}
              <span className="plan-intro-bubble__inline-text">
                {t('task.ai.for', { defaultValue: 'for' })}
              </span>
              <button
                type="button"
                className="plan-intro-bubble__date-pill"
                onClick={(event) => setDatePickerAnchor(event.currentTarget)}
              >
                {planDateLabel}
              </button>
              <span className="plan-intro-bubble__inline-text">.</span>
              <AddPreferenceChip
                value={customText}
                onChangeText={setCustomText}
                placeholder={t('planMyDayIntro.placeholder', {
                  defaultValue: 'Anything else I should know?',
                })}
              />
            </div>
          </div>
        </div>
      </div>

      <Popover
        open={Boolean(datePickerAnchor)}
        anchorEl={datePickerAnchor}
        onClose={() => setDatePickerAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        className="default-popover plan-intro-bubble__date-popover"
      >
        <DateCalendar
          value={planDate}
          onChange={(nextDate) => {
            if (!nextDate) return;
            setPlanDate(nextDate);
            setDatePickerAnchor(null);
          }}
          shouldDisableDate={(date) => date.isBefore(dayjs().startOf('day'))}
        />
      </Popover>

      <PlanTypeDetailDrawer
        type={expandedType}
        label={expandedType ? t(`task.allPlan.planTypeLabels.${expandedType}`) : ''}
        anchorEl={expandAnchorEl}
        taskScheduler={taskScheduler}
        targetDate={planDate.format('YYYY-MM-DD')}
        onClose={closeExpandPopover}
      />
    </>
  );
};

export default PlanIntroBubble;
